import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Coins,
  Trophy,
  XCircle,
  BarChart3,
  Loader2,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  getUserStats,
  getDefaultStats,
  calculateWinRate,
  formatStatsCurrency,
  formatStatsNumber,
} from '../services/statsService'
import type { UserStats, GameStats } from '@/types'

interface UserStatsPanelProps {
  userId: string
  isDemo?: boolean
}

export function UserStatsPanel({ userId, isDemo = false }: UserStatsPanelProps) {
  const { t } = useTranslation()
  const [stats, setStats] = useState<UserStats>(getDefaultStats())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [userId])

  const loadStats = async () => {
    if (isDemo) {
      // For demo mode, show empty stats
      setStats(getDefaultStats())
      setLoading(false)
      return
    }

    setLoading(true)
    const userStats = await getUserStats(userId)
    setStats(userStats)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
      </div>
    )
  }

  const netResult = stats.totalWon - stats.totalLost

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Coins className="w-5 h-5" />}
          label={t('stats.totalWagered')}
          value={formatStatsCurrency(stats.totalWagered)}
          color="text-casino-gold"
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label={t('stats.totalWon')}
          value={formatStatsCurrency(stats.totalWon)}
          color="text-green-400"
        />
        <StatCard
          icon={<TrendingDown className="w-5 h-5" />}
          label={t('stats.totalLost')}
          value={formatStatsCurrency(stats.totalLost)}
          color="text-red-400"
        />
        <StatCard
          icon={netResult >= 0 ? <Trophy className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          label={t('stats.netResult')}
          value={formatStatsCurrency(netResult)}
          color={netResult >= 0 ? 'text-green-400' : 'text-red-400'}
          highlight
        />
      </div>

      {/* Overall Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-casino-gold" />
            {t('stats.byGame')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="w-full bg-zinc-800/50">
              <TabsTrigger value="all" className="flex-1">
                Todos
              </TabsTrigger>
              <TabsTrigger value="ruleta" className="flex-1">
                Ruleta
              </TabsTrigger>
              <TabsTrigger value="blackjack" className="flex-1">
                Blackjack
              </TabsTrigger>
              <TabsTrigger value="baccarat" className="flex-1">
                Baccarat
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-4">
              <GameStatsGrid
                stats={{
                  wagered: stats.totalWagered,
                  won: stats.totalWon,
                  lost: stats.totalLost,
                  bets: stats.totalBets,
                  wins: stats.totalWins,
                  losses: stats.totalLosses,
                }}
              />
            </TabsContent>

            <TabsContent value="ruleta" className="mt-4">
              <GameStatsGrid stats={stats.ruleta || getDefaultStats().ruleta!} />
            </TabsContent>

            <TabsContent value="blackjack" className="mt-4">
              <GameStatsGrid stats={stats.blackjack || getDefaultStats().blackjack!} />
            </TabsContent>

            <TabsContent value="baccarat" className="mt-4">
              <GameStatsGrid stats={stats.baccarat || getDefaultStats().baccarat!} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {isDemo && (
        <p className="text-center text-gray-500 text-sm">
          Las estadísticas solo se registran con cuenta real
        </p>
      )}
    </div>
  )
}

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string
  color: string
  highlight?: boolean
}

function StatCard({ icon, label, value, color, highlight }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg ${
        highlight
          ? 'bg-gradient-to-br from-casino-gold/20 to-casino-gold/5 border border-casino-gold/30'
          : 'bg-zinc-800/50'
      }`}
    >
      <div className={`flex items-center gap-2 mb-2 ${color}`}>
        {icon}
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </motion.div>
  )
}

// Game Stats Grid
interface GameStatsGridProps {
  stats: GameStats
}

function GameStatsGrid({ stats }: GameStatsGridProps) {
  const { t } = useTranslation()
  const winRate = calculateWinRate(stats.wins, stats.bets)
  const netResult = stats.won - stats.lost

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div className="p-4 bg-zinc-800/30 rounded-lg">
        <p className="text-xs text-gray-400 mb-1">{t('stats.bets')}</p>
        <p className="text-xl font-bold text-white">{formatStatsNumber(stats.bets)}</p>
      </div>

      <div className="p-4 bg-zinc-800/30 rounded-lg">
        <p className="text-xs text-gray-400 mb-1">{t('stats.wagered')}</p>
        <p className="text-xl font-bold text-casino-gold">{formatStatsCurrency(stats.wagered)}</p>
      </div>

      <div className="p-4 bg-zinc-800/30 rounded-lg">
        <p className="text-xs text-gray-400 mb-1">{t('stats.won')}</p>
        <p className="text-xl font-bold text-green-400">{formatStatsCurrency(stats.won)}</p>
      </div>

      <div className="p-4 bg-zinc-800/30 rounded-lg">
        <p className="text-xs text-gray-400 mb-1">{t('stats.winRate')}</p>
        <div className="flex items-center gap-2">
          <p className={`text-xl font-bold ${winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
            {winRate}%
          </p>
          <span className="text-xs text-gray-500">
            ({stats.wins}W / {stats.losses}L)
          </span>
        </div>
      </div>

      <div className="p-4 bg-zinc-800/30 rounded-lg col-span-2 md:col-span-2">
        <p className="text-xs text-gray-400 mb-1">{t('stats.netResult')}</p>
        <p className={`text-xl font-bold ${netResult >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {netResult >= 0 ? '+' : ''}{formatStatsCurrency(netResult)}
        </p>
      </div>
    </div>
  )
}

export default UserStatsPanel
